<script lang="ts" setup>
import { ref } from 'vue'
import type { Role } from '@/types/rbac'

defineProps<{
  roles: Role[]
}>()

const expandedRoleIds = ref<Set<number>>(new Set())

const toggleExpand = (roleId: number) => {
  if (expandedRoleIds.value.has(roleId)) {
    expandedRoleIds.value.delete(roleId)
  } else {
    expandedRoleIds.value.add(roleId)
  }
  expandedRoleIds.value = new Set(expandedRoleIds.value)
}
</script>

<template>
  <div class="roles-list-container">
    <!-- Empty State -->
    <div v-if="roles.length === 0" class="empty-state">
      <v-icon icon="mdi-shield-off-outline" size="48" />
      <span class="empty-title">{{ $t('dashboard.admin.roles.empty') }}</span>
      <span class="empty-description">{{ $t('dashboard.admin.roles.emptyDescription') }}</span>
    </div>

    <!-- Roles Table -->
    <div v-else class="roles-table-wrapper">
      <table class="roles-table">
        <thead>
          <tr>
            <th class="col-name">{{ $t('dashboard.admin.roles.columns.displayName') }}</th>
            <th class="col-description">{{ $t('dashboard.admin.roles.columns.description') }}</th>
            <th class="col-priority">{{ $t('dashboard.admin.roles.columns.priority') }}</th>
            <th class="col-permissions">{{ $t('dashboard.admin.roles.columns.permissions') }}</th>
            <th class="col-system">{{ $t('dashboard.admin.roles.columns.system') }}</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="role in roles" :key="role.id">
            <tr class="role-row" :class="{ expanded: expandedRoleIds.has(role.id) }">
              <td class="col-name">
                <div class="name-cell">
                  <v-icon icon="mdi-shield-account" size="18" />
                  <div class="name-content">
                    <span class="display-name">{{ role.display_name }}</span>
                    <span class="internal-name">{{ role.name }}</span>
                  </div>
                </div>
              </td>
              <td class="col-description">
                <span class="description-text">{{ role.description || '-' }}</span>
              </td>
              <td class="col-priority">
                <v-chip size="x-small" variant="flat" color="grey">
                  {{ role.priority }}
                </v-chip>
              </td>
              <td class="col-permissions">
                <button
                  v-if="role.permissions && role.permissions.length > 0"
                  class="permissions-toggle"
                  @click="toggleExpand(role.id)"
                >
                  <span>{{ $t('dashboard.admin.roles.permissionCount', { count: role.permissions.length }, role.permissions.length) }}</span>
                  <v-icon
                    :icon="expandedRoleIds.has(role.id) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    size="16"
                  />
                </button>
                <span v-else class="no-permissions">-</span>
              </td>
              <td class="col-system">
                <v-chip
                  v-if="role.system"
                  size="x-small"
                  color="warning"
                  variant="tonal"
                >
                  {{ $t('dashboard.admin.roles.systemBadge') }}
                </v-chip>
              </td>
            </tr>
            <tr v-if="expandedRoleIds.has(role.id) && role.permissions" class="permissions-row">
              <td colspan="5">
                <div class="permissions-content">
                  <span
                    v-for="perm in role.permissions"
                    :key="perm"
                    class="permission-chip"
                  >
                    {{ perm }}
                  </span>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.roles-list-container {
  background: rgba(15, 23, 48, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: var(--dashboard-card-radius, 16px);
  overflow: hidden;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 48px 16px;
  text-align: center;
  color: rgba(200, 218, 255, 0.4);

  .empty-title {
    font-weight: 500;
    color: rgba(200, 218, 255, 0.6);
  }

  .empty-description {
    font-size: 0.85rem;
  }
}

.roles-table-wrapper {
  overflow-x: auto;
}

.roles-table {
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(200, 218, 255, 0.5);
    background: rgba(0, 0, 0, 0.15);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }
}

.role-row {
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.05);
  }

  &.expanded {
    background: rgba(120, 163, 255, 0.08);
  }
}

.permissions-row {
  td {
    padding: 0 !important;
    background: rgba(0, 0, 0, 0.1);
  }
}

.permissions-content {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
}

.permission-chip {
  padding: 4px 10px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 4px;
  font-size: 0.75rem;
  font-family: monospace;
  color: rgba(200, 218, 255, 0.8);
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.name-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.display-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.internal-name {
  font-size: 0.75rem;
  font-family: monospace;
  color: rgba(200, 218, 255, 0.5);
}

.description-text {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.permissions-toggle {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(120, 163, 255, 0.1);
  border: 1px solid rgba(120, 163, 255, 0.2);
  border-radius: 4px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(120, 163, 255, 0.2);
  }
}

.no-permissions {
  color: rgba(200, 218, 255, 0.4);
}

/* Column widths */
.col-name {
  min-width: 200px;
}

.col-description {
  min-width: 250px;
}

.col-priority {
  width: 100px;
  text-align: center;
}

.col-permissions {
  width: 150px;
}

.col-system {
  width: 120px;
  text-align: center;
}

/* Light Theme */
.v-theme--lightTheme {
  .roles-list-container {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .roles-table {
    th {
      color: rgba(0, 0, 0, 0.54);
      background: rgba(0, 0, 0, 0.03);
    }

    td {
      border-color: rgba(0, 0, 0, 0.08);
    }
  }

  .role-row:hover {
    background: rgba(28, 110, 227, 0.04);
  }

  .role-row.expanded {
    background: rgba(28, 110, 227, 0.06);
  }

  .permissions-row td {
    background: rgba(0, 0, 0, 0.02);
  }

  .display-name {
    color: rgba(0, 0, 0, 0.87);
  }

  .internal-name {
    color: rgba(0, 0, 0, 0.54);
  }

  .description-text {
    color: rgba(0, 0, 0, 0.7);
  }

  .permission-chip {
    background: rgba(28, 110, 227, 0.08);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(0, 0, 0, 0.7);
  }

  .permissions-toggle {
    background: rgba(28, 110, 227, 0.08);
    border-color: rgba(28, 110, 227, 0.15);
    color: rgba(0, 0, 0, 0.7);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .col-description,
  .col-priority,
  .col-system {
    display: none;
  }
}
</style>
